<template>
  <v-container>
    <v-form ref="form" v-model="isFormValid" lazy-validation>
      <v-row>
        <v-col cols="12">
          <v-card class="px-7 mt-4 pt-7">
            <v-row>
              <v-col cols="12">
                <SimplePageHeader
                  v-if="!isEdit"
                  title="Add Client"
                ></SimplePageHeader>
                <SimplePageHeader
                  title="Edit Client"
                  v-else
                >
                  <template v-slot:right-buttons>
                    <v-switch
                      v-model="active"
                      label="Active"
                      class="float-end"
                      :readonly="isReadOnlyAccess"
                    ></v-switch>
                  </template>
                </SimplePageHeader>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-tabs v-model="tab">
                  <v-tab :key="CLIENT">
                    Client
                  </v-tab>
                  <v-tab :key="STAKEHOLDERS">
                    Stakeholders
                  </v-tab>
                  <v-tab :key="DEFAULT_EXPENSES">
                    Default Expenses
                  </v-tab>
                </v-tabs>
                <v-tabs-items v-model="tab">
                  <v-tab-item :key="CLIENT">
                    <v-row class="py-5">
                      <v-col cols="12" sm="6" md="3" xl="2">
                        <v-text-field
                          label="Name"
                          v-model="name"
                          :rules="[Required('Client Name')]"
                          validate-on-blur
                          outlined
                          :readonly="isReadOnlyAccess"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6" md="3" xl="2">
                        <InvoiceFrequenciesDropDownList
                          :id="invoiceFrequencyId"
                          :isRequired="true"
                          :read-only="isReadOnlyAccess"
                          @change="(newValue) => this.invoiceFrequencyId = newValue"
                        ></InvoiceFrequenciesDropDownList>
                      </v-col>
                      <v-col cols="12" sm="6" md="3" lg="3" xl="2">
                        <ThirdPartyPortalsDropDownList
                          :id="thirdPartyPortalId"
                          :isRequired="false"
                          :clearable="!isReadOnlyAccess"
                          :readonly="isReadOnlyAccess"
                          @ThirdPartyPortalIdChange="(newValue) => this.thirdPartyPortalId = newValue"
                        ></ThirdPartyPortalsDropDownList>
                      </v-col>
                      <v-col cols="12" sm="6" md="3" xl="2">
                        <FormattedNumberField
                          label="VMS Fee"
                          :value="vmsFee"
                          append-icon="percent"
                          :readonly="isReadOnlyAccess"
                          :clearable="true"
                          @valueChange="(newValue) => this.vmsFee = newValue"
                        ></FormattedNumberField>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                  <v-tab-item :key="STAKEHOLDERS">
                    <ClientStakeHolderCollector
                      class="py-5"
                      label="Send Invoices To: (Client Stakeholder)"
                      :recipientsList="clientStakeholderRecipients"
                      :readonly="isReadOnlyAccess"
                      :disabled="false"
                      @nameChange="(id, newValue) => this.clientStakeholderRecipients[id]['stakeholderName'] = newValue"
                      @emailChange="(id, newValue) => this.clientStakeholderRecipients[id]['emailAddress'] = newValue"
                      @notesChange="(id, newValue) => this.clientStakeholderRecipients[id]['notes'] = newValue"
                      @deleteRecipient="(id) => this.clientStakeholderRecipients.splice(id, 1)"
                      @addRecipient="() => this.clientStakeholderRecipients.push({stakeholderName: '', emailAddress: '', notes: ''})"
                    ></ClientStakeHolderCollector>
                  </v-tab-item>
                  <v-tab-item :key="DEFAULT_EXPENSES">
                    <v-row class="my-5">
                      <v-col cols="12">
                        <ExpensesCard
                          class="px-2"
                          cardTitle="Contractor"
                          :expensesList="contractorExpenses"
                          :readonly="isReadOnlyAccess"
                          :showActive="true"
                          :showExpenseAmountType="true"
                          @expensesListChange="(newValue) => this.contractorExpenses = newValue"
                        ></ExpensesCard>
                      </v-col>
                    </v-row>
                    <v-divider></v-divider>
                    <v-row class="my-5">
                      <v-col cols="12">
                        <ExpensesCard
                          class="px-2"
                          cardTitle="Perm. Placements"
                          :expensesList="directPlacementsExpenses"
                          :readonly="isReadOnlyAccess"
                          :showActive="true"
                          @expensesListChange="(newValue) => this.directPlacementsExpenses = newValue"
                        ></ExpensesCard>
                      </v-col>
                    </v-row>
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row justify="end" v-if="isReadOnlyAccess">
        <v-col cols="6" md="3" class="pl-2">
          <v-btn
            block
            outlined
            depressed
            @click="cancel"
          >
            Back
          </v-btn>
        </v-col>
      </v-row>
      <ButtonsSubmitForm
        v-if="!isReadOnlyAccess"
        :isFormValid="isFormValid"
        @save="handleSaveClient"
        @cancel="cancel"
      >
      </ButtonsSubmitForm>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { clientsRoute } from '@/lib/constants/routes'
import { ButtonsSubmitForm } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { InvoiceFrequenciesDropDownList } from '@/components/invoiceFrequencies'
import Required from '@/lib/formValidations/required'
import ThirdPartyPortalsDropDownList from '@/components/clients/ThirdPartyPortalsDropDownList'
import ClientStakeHolderCollector from '@/components/emails/ClientStakeHolderCollector'
import { ExpensesCard } from '@/components/expenses'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { ADMIN, MANAGER, SALES_PERSON } from '@/lib/constants/securityRoles'
import { FormattedNumberField } from '@/components/shared/textFields'

export default {
  components: {
    ButtonsSubmitForm,
    SimplePageHeader,
    InvoiceFrequenciesDropDownList,
    ThirdPartyPortalsDropDownList,
    ClientStakeHolderCollector,
    ExpensesCard,
    FormattedNumberField
  },
  data: () => ({
    isFormValid: false,
    tab: null,
    CLIENT: 1,
    STAKEHOLDERS: 2,
    DEFAULT_EXPENSES: 3
  }),
  computed: {
    ...mapFields('client', [
      'name',
      'active',
      'invoiceFrequencyId',
      'thirdPartyPortalId',
      'clientStakeholderRecipients',
      'vmsFee',
      'contractorExpenses',
      'directPlacementsExpenses',
      'zohoClientId'
    ]),
    id: {
      get () {
        return this.$route.params.id || null
      }
    },
    isEdit: {
      get () {
        return !!this.id
      }
    },
    isReadOnlyAccess () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    }
  },
  methods: {
    getCurrentUser,
    userHasRoles,
    Required,
    ...mapActions('client', [
      'getClient',
      'saveClient',
      'clearClient'
    ]),
    loadClientData () {
      this.getClient({ id: this.id })
    },
    handleSaveClient () {
      if (this.$refs.form.validate()) {
        this.saveClient()
          .then(() => this.$router.push({ name: clientsRoute.name }))
      }
    },
    handleInvoiceFrequencyChange (value) {
      this.invoiceFrequencyId = value
    },
    cancel () {
      this.$router.push({ name: clientsRoute.name })
    }
  },
  mounted () {
    this.$refs.form.resetValidation()
    if (this.isEdit) {
      this.loadClientData()
    } else {
      this.clearClient()
    }
  }
}
</script>
